import { AlarmLevel } from 'icreate/common/models/alarm-level';
import { Channel } from 'icreate/common/models/channel';
import { GaugeGraph, GaugeGraphChannel } from 'icreate/common/models/graphs';
import { DefaultColorName, SystemColor } from 'icreate/common/models/system-color';

export async function printGaugeGraph(graph: GaugeGraph, echarts, systemColors: SystemColor[], oldGraph?: GaugeGraph) {
    const gaugeChannels: GaugeGraphChannel[] = graph.channels;

    if (!gaugeChannels) return;

    const oldChannelsIdsMap: Map<string, GaugeGraphChannel> = new Map();

    oldGraph?.channels?.forEach((channel: GaugeGraphChannel) => {
        const graphChannel = channel.channel.id;
        oldChannelsIdsMap.set(graphChannel, channel);
    });

    const titleBorder =
        oldGraph && oldGraph?.title != graph.title ? [true, true, true, true] : [false, false, false, false];

    const updatedCells = new Set<number>();

    gaugeChannels.forEach((channel: GaugeGraphChannel) => {
        if (!oldGraph) return;
        if (!oldChannelsIdsMap.has(channel.channel.id)) updatedCells.add(channel.rowNumber);
        else {
            const oldChannel = oldChannelsIdsMap.get(channel.channel.id);
            if (oldChannel.rowNumber !== channel.rowNumber) {
                updatedCells.add(channel.rowNumber);
            }
        }
    });

    const cell1 = getTableCell(getGraphChannel(gaugeChannels, 0), updatedCells, systemColors, echarts, true);
    const cell2 = getTableCell(getGraphChannel(gaugeChannels, 1), updatedCells, systemColors, echarts);
    const cell3 = getTableCell(getGraphChannel(gaugeChannels, 2), updatedCells, systemColors, echarts);
    const cell4 = getTableCell(getGraphChannel(gaugeChannels, 3), updatedCells, systemColors, echarts);
    const cell5 = getTableCell(getGraphChannel(gaugeChannels, 4), updatedCells, systemColors, echarts, true, false);
    const cell6 = getTableCell(getGraphChannel(gaugeChannels, 5), updatedCells, systemColors, echarts, false, false);
    const cell7 = getTableCell(getGraphChannel(gaugeChannels, 6), updatedCells, systemColors, echarts, false, false);
    const cell8 = getTableCell(getGraphChannel(gaugeChannels, 7), updatedCells, systemColors, echarts, false, false);

    const tableContent = [
        [
            {
                columns: [
                    { width: '*', text: '' },
                    {
                        width: 'auto',
                        table: {
                            body: [
                                [
                                    {
                                        text: graph.title,
                                        border: titleBorder,
                                        borderColor: ['red', 'red', 'red', 'red'],
                                        fontSize: 15,
                                        alignment: 'center',
                                        noWrap: true,
                                    },
                                ],
                            ],
                        },
                    },
                    { width: '*', text: '' },
                ],
                margin: [0, 10, 0, 10],
            },
            {
                columns: [
                    { width: '*', text: '' },
                    {
                        width: 'auto',
                        table: {
                            heights: [205, 205],
                            widths: [165, 165, 165, 165],
                            body: [
                                [cell1, cell2, cell3, cell4],
                                [cell5, cell6, cell7, cell8],
                            ],
                            alignment: 'center',
                        },
                        alignment: 'center',
                        margin: [0, 0, 0, 0],
                        layout: {
                            hLineWidth: () => 0,
                            vLineWidth: () => 0,
                            paddingLeft: () => 0,
                            paddingRight: () => 0,
                            paddingTop: () => 0,
                            paddingBottom: () => 0,
                        },
                    },
                    { width: '*', text: '' },
                ],
            },
        ],
    ];

    return {
        body: [tableContent],
        widths: [763],
        heights: [483],
    };
}

function getGraphChannel(channels: GaugeGraphChannel[], position: number) {
    return channels.find(channel => channel.rowNumber == position);
}

function getTableCell(
    channel: GaugeGraphChannel,
    updatedCells: Set<number>,
    systemColors: SystemColor[],
    echarts,
    hasLeftBorder: boolean = false,
    hasTopBorder: boolean = true
) {
    const hasChanges = channel ? updatedCells.has(channel.rowNumber) : false;
    const dataUrl = channel ? getDataUrl(channel.channel, channel.scaleDivision, systemColors, echarts) : '';
    const borderColor = hasChanges ? 'red' : 'black';
    const borderWidth = hasChanges ? 2 : 1;

    const channelTag = channel?.channel?.opsData?.tag ?? '';
    const channelName = channel?.channel?.opsData?.name ?? '';

    let width = 165;
    let height = 205;

    if (hasChanges) {
        width = 164;
        height = 204;
    }

    const imageCell = !channel
        ? { text: '' }
        : {
              image: dataUrl,
              width: 150,
              height: 150,
              alignment: 'center',
          };

    return {
        table: {
            widths: [width],
            heights: [height],
            body: [
                [
                    {
                        stack: [
                            imageCell,
                            {
                                text: channelTag,
                                alignment: 'center',
                            },
                            {
                                text: channelName,
                                alignment: 'center',
                            },
                        ],
                        border: [hasChanges ? true : hasLeftBorder, hasChanges ? true : hasTopBorder, true, true],
                        margin: [0, 0, 0, 0],
                    },
                ],
            ],
        },
        layout: {
            hLineWidth: () => borderWidth,
            vLineWidth: () => borderWidth,
            hLineColor: () => borderColor,
            vLineColor: () => borderColor,
            paddingLeft: () => 0,
            paddingRight: () => 0,
            paddingTop: () => 0,
            paddingBottom: () => 0,
        },
        margin: [0, 0, 0, 0],
    };
}

export enum FlagOption {
    UNDEFINED = 'undefined',
    HIGH_HIGH = 'highHigh',
    LOW_LOW = 'lowlow',
    HIGH = 'high',
    LOW = 'low',
    NORMAL = 'normal',
    FEEDBACK = 'feedbackError',
    SYSTEM_CHANNEL = 'systemError',
    DIGITAL = 'digital',
    OVER = 'over',
    UNDER = 'under',
}

export const FLAG_UNDEFINED_COLOR = '#20e420';
export const GRAPH_UNDEFINED_COLOR = '#9cb2fb';

export function getAlarmColors(systemColors: SystemColor[]) {
    const colorsList = Array.isArray(systemColors) ? systemColors : [];
    const getColorValue = (name: string, fallback: string) => {
        const found = colorsList.find(c => c.name === name);
        return found && found.color ? found.color : fallback;
    };

    return {
        Alarm: getColorValue(DefaultColorName.ALARM, '#ff1b1b'),
        Caution: getColorValue(DefaultColorName.CAUTION, '#fad900'),
        Warning: getColorValue(DefaultColorName.WARNING, '#f69400'),
        None: FLAG_UNDEFINED_COLOR,
    };
}

function getDataUrl(channel: Channel, divisions: number, systemColors: SystemColor[], echarts): string {
    const echartsInstance = echarts.default || echarts;

    let gaugeGraphDivision = document.createElement('div');
    gaugeGraphDivision.style.height = '280px';
    gaugeGraphDivision.style.width = '253px';

    let myChart = echartsInstance.init(gaugeGraphDivision, null, { width: 253, height: 280 });
    const scale = channel.fcuData.scale || 1;
    const minScaled = channel.fcuData.measurableRange.min / scale;
    const maxScaled = channel.fcuData.measurableRange.max / scale;
    const rangeOffset = 1 / scale;

    const fcuAlarms = channel.fcuData;
    const veryLow = toRange(
        minScaled,
        fcuAlarms.alarmSettingVeryLow.setPoint / scale,
        rangeOffset,
        !fcuAlarms.alarmSettingVeryLow.enable
    );
    const low = toRange(
        fcuAlarms.alarmSettingVeryLow.enable ? veryLow.max : minScaled,
        fcuAlarms.alarmSettingLow.setPoint / scale,
        rangeOffset,
        !fcuAlarms.alarmSettingLow.enable
    );
    const veryHigh = toRange(
        fcuAlarms.alarmSettingVeryHigh.setPoint / scale,
        maxScaled,
        rangeOffset,
        !fcuAlarms.alarmSettingVeryHigh.enable
    );
    const high = toRange(
        fcuAlarms.alarmSettingHigh.setPoint / scale,
        fcuAlarms.alarmSettingVeryHigh.enable ? veryHigh.min : maxScaled,
        rangeOffset,
        !fcuAlarms.alarmSettingHigh.enable
    );

    const hasNormalRange = fcuAlarms.normalRange.min !== 0 || fcuAlarms.normalRange.max !== 0;
    const normal = toRange(fcuAlarms.normalRange.min, fcuAlarms.normalRange.max, rangeOffset, !hasNormalRange);

    const withoutNormalRange = ((high.min || maxScaled) - (low.max || minScaled)) / 2;
    const lowToNormalGap = gapRange(
        low.max || minScaled,
        hasNormalRange ? normal.min : (low.max || minScaled) + withoutNormalRange,
        rangeOffset,
        maxScaled
    );
    const normalToHighGap = gapRange(
        hasNormalRange ? normal.max : lowToNormalGap.max,
        high.min || maxScaled,
        rangeOffset,
        maxScaled
    );

    const rangeMap = new Map<any, { min: number; max: number }>();
    rangeMap.set(FlagOption.LOW_LOW, veryLow);
    rangeMap.set(FlagOption.LOW, low);
    rangeMap.set('lowToNormal', lowToNormalGap);
    rangeMap.set(FlagOption.NORMAL, normal);
    rangeMap.set('normalToHigh', normalToHighGap);
    rangeMap.set(FlagOption.HIGH, high);
    rangeMap.set(FlagOption.HIGH_HIGH, veryHigh);

    let xAxisColorArr: [number, string][] = [];
    let totalRange = Math.abs(maxScaled - minScaled);
    let rangeKeys = Array.from(rangeMap.keys());
    let previewRange = 0;

    const alarmColors = getAlarmColors(systemColors);

    for (let i = 0; i < rangeKeys.length; i++) {
        let rangeKey = rangeKeys[i];
        let range = rangeMap.get(rangeKey);

        if (!range) continue;

        let percentage = previewRange + (range.max - range.min) / totalRange;
        if (percentage > 0) {
            let color;
            if (rangeKey === FlagOption.NORMAL || rangeKey === 'lowToNormal' || rangeKey === 'normalToHigh') {
                color = FLAG_UNDEFINED_COLOR;
            } else {
                color = getAlarmStateColor(rangeKey, channel, alarmColors);
            }

            const safeColor = color && typeof color === 'string' ? color : FLAG_UNDEFINED_COLOR;
            const safePercentage = Math.min(Math.max(percentage, 0), 1);

            xAxisColorArr.push([safePercentage, safeColor]);
        }
        previewRange = previewRange + (range.max - range.min) / totalRange;
    }

    if (xAxisColorArr.length === 0) {
        xAxisColorArr.push([1, FLAG_UNDEFINED_COLOR]);
    }

    let randomRaw =
        Math.floor(Math.random() * (fcuAlarms.measurableRange.max - fcuAlarms.measurableRange.min + 1)) +
        fcuAlarms.measurableRange.min;
    const decimalPlaces = getDecimalPlaces(channel);
    let randomNumberScaled = Number((randomRaw / scale).toFixed(decimalPlaces));

    const maskValue = String(randomNumberScaled).replace(/\d/g, 'x');

    const option = {
        series: [
            {
                type: 'gauge',
                radius: '100%',
                center: ['50%', '60%'],
                min: minScaled,
                max: maxScaled,
                splitNumber: divisions,

                detail: {
                    show: true,
                    fontSize: 14,
                    fontWeight: 'bold',
                    color: 'black',
                    offsetCenter: [0, '55%'],
                    formatter: (value: number) => {
                        return `${maskValue}\n ${channel.opsData.measurementUnit}`;
                    },
                },

                pointer: {
                    itemStyle: {
                        color: 'black',
                    },
                    show: true,
                    width: 3,
                },
                axisLine: {
                    lineStyle: {
                        width: 15,
                        color: xAxisColorArr,
                        opacity: 1,
                    },
                },
                axisTick: {
                    length: 5,
                    lineStyle: {
                        color: 'auto',
                        width: 3,
                    },
                },
                axisLabel: {
                    distance: 15,
                    fontSize: 8,
                    color: 'black',
                    formatter: (value: number) => {
                        return value % 1 !== 0 ? value.toFixed(2) : value.toFixed(0);
                    },
                },
                splitLine: {
                    length: 15,
                    lineStyle: {
                        color: 'auto',
                        width: 3,
                    },
                },
                data: [
                    {
                        value: randomNumberScaled,
                    },
                ],
            },
        ],
    };

    myChart.setOption(option);

    const dataUrl = myChart.getDataURL({
        type: 'png',
        pixelRatio: 2,
        backgroundColor: '#fff',
    });

    myChart.dispose();
    return dataUrl;
}

function toRange(min: number, max: number, rangeOffset, zeroOut = false) {
    if (max - min === 0 && !zeroOut) max += rangeOffset;
    return zeroOut ? { min: 0, max: 0 } : { min, max };
}

function gapRange(min: number, max: number, rangeOffset, maxScaled) {
    if (max - min <= rangeOffset) return { min: 0, max: 0 };
    if (max !== maxScaled) max -= rangeOffset;
    return { min, max };
}

function getDecimalPlaces(channel: Channel): number {
    const scale = channel?.fcuData.scale || 1;
    return Math.max(Math.round(Math.log10(scale)), 0);
}

export function getAlarmStateColor(alarmState, channel, alarmColors) {
    const alarmLevelMap = {
        [AlarmLevel.Caution]: 'Caution',
        [AlarmLevel.Warning]: 'Warning',
        [AlarmLevel.Alarm]: 'Alarm',
        [AlarmLevel.None]: 'None',
    };

    let levelKey = 'None';
    if (alarmState === FlagOption.LOW) {
        levelKey = alarmLevelMap[channel.fcuData.alarmSettingLow?.alarmLevel] || 'None';
    } else if (alarmState === FlagOption.LOW_LOW) {
        levelKey = alarmLevelMap[channel.fcuData.alarmSettingVeryLow?.alarmLevel] || 'None';
    } else if (alarmState === FlagOption.HIGH) {
        levelKey = alarmLevelMap[channel.fcuData.alarmSettingHigh?.alarmLevel] || 'None';
    } else if (alarmState === FlagOption.HIGH_HIGH) {
        levelKey = alarmLevelMap[channel.fcuData.alarmSettingVeryHigh?.alarmLevel] || 'None';
    } else if (alarmState === FlagOption.DIGITAL) {
        levelKey = alarmLevelMap[channel.fcuData.digitalAlarmSetting?.alarmLevel] || 'None';
    } else if (alarmState === FlagOption.UNDEFINED) {
        return GRAPH_UNDEFINED_COLOR;
    }

    return alarmColors[levelKey] || GRAPH_UNDEFINED_COLOR;
}
